import { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Barcode, MagnifyingGlass, Camera, X, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditShizai } from '@/auth/AuthContext';
import { shizaiApi, ORDER_STATUS_BADGE, yen } from './shizaiApi';

const REGION = 'shizai-scan-region';

export default function Receiving() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditShizai(user);
  const [code, setCode] = useState('');
  const [order, setOrder] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [qtys, setQtys] = useState({});
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    shizaiApi.locations().then((ls) => {
      setLocations(ls);
      const shelf = ls.find((l) => l.code === '資材棚');
      setLocationId(shelf?.id || (ls[0]?.id || ''));
    }).catch((err) => console.error('[shizai] locations failed', err));
  }, []);

  const doLookup = useCallback(async (c) => {
    const v = (c ?? code).trim();
    if (!v) return;
    try {
      const o = await shizaiApi.lookup(v);
      setOrder(o); setQtys({});
      toast.success(o.order_no);
    } catch (e) {
      setOrder(null);
      toast.error(e?.response?.data?.detail || t('shizai.receive.notFound'));
    }
  }, [code, t]);

  const stopScan = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const startScan = async () => {
    setScanning(true);
    try {
      const mod = await import('html5-qrcode');
      const inst = new mod.Html5Qrcode(REGION);
      scannerRef.current = inst;
      await inst.start({ facingMode: 'environment' }, { fps: 10, qrbox: 240 },
        async (decoded) => { await stopScan(); setCode(decoded); doLookup(decoded); },
        () => { /* per-frame errors ignored */ });
    } catch { setScanning(false); toast.error(t('shizai.error')); }
  };

  useEffect(() => () => { stopScan(); }, [stopScan]);

  const confirmReceive = async () => {
    if (!order) return;
    const lines = Object.entries(qtys)
      .map(([idx, q]) => ({ line_index: Number(idx), received_qty: Number(q) || 0 }))
      .filter((l) => l.received_qty > 0);
    if (lines.length === 0) { toast.error(t('shizai.receive.qtyThisTime')); return; }
    setBusy(true);
    try {
      const updated = await shizaiApi.receiveOrder(order.id, { lines, location_id: locationId || null });
      toast.success(t('shizai.receive.done'));
      setOrder(updated); setQtys({});
    } catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-receiving">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2"><Barcode size={22} className="text-rose-500" weight="duotone" /> {t('shizai.receive.title')}</h1>
        <p className="text-sm text-zinc-500 mt-1">{t('shizai.receive.scanHint')}</p>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 space-y-3 max-w-xl">
        <div id={REGION} className={`${scanning ? 'block' : 'hidden'} rounded-lg overflow-hidden bg-black`} />
        {!scanning
          ? <Button variant="outline" onClick={startScan} data-testid="receiving-scan-start" className="w-full h-11 border-rose-200 text-rose-600 hover:bg-rose-50"><Camera size={17} className="mr-1.5" /> {t('shizai.receive.scanStart')}</Button>
          : <Button variant="outline" onClick={stopScan} data-testid="receiving-scan-stop" className="w-full h-11"><X size={17} className="mr-1.5" /> 停止</Button>}
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doLookup()} placeholder={t('shizai.receive.manual')} className="h-11 font-mono" data-testid="receiving-manual-input" />
          <Button onClick={() => doLookup()} data-testid="receiving-lookup-btn" className="h-11 bg-teal-700 hover:bg-teal-800"><MagnifyingGlass size={17} /></Button>
        </div>
        <p className="text-[11px] text-zinc-400">{t('shizai.receive.toShelf')}</p>
      </div>

      {order && (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 space-y-4 max-w-2xl" data-testid="receiving-order">
          <div className="flex items-center gap-3 flex-wrap">
            <Package size={20} className="text-rose-500" weight="duotone" />
            <span className="font-mono font-semibold text-zinc-800">{order.order_no}</span>
            <Badge variant="outline" className={ORDER_STATUS_BADGE[order.status]}>{t(`shizai.orderStatus.${order.status}`)}</Badge>
            <span className="text-sm text-zinc-500">{order.supplier?.name || ''}</span>
            <span className="ml-auto text-sm font-semibold text-zinc-700">{yen(order.total)}</span>
          </div>

          {order.status === 'received' || order.status === 'cancelled' ? (
            <p className="text-sm text-zinc-500">この発注は{t(`shizai.orderStatus.${order.status}`)}です。</p>
          ) : (
            <>
              <div className="max-w-xs"><Label className="text-xs font-semibold text-zinc-600">{t('shizai.receive.location')}</Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger data-testid="receiving-location-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}{l.name ? ` — ${l.name}` : ''}</SelectItem>)}</SelectContent>
                </Select></div>

              <table className="w-full text-sm">
                <thead><tr className="text-zinc-500 text-xs border-b border-zinc-200">
                  <th className="text-left font-semibold py-2">{t('shizai.req.name')}</th>
                  <th className="text-left font-semibold py-2">{t('shizai.req.partNo')}</th>
                  <th className="text-right font-semibold py-2">{t('shizai.receive.remaining')}</th>
                  <th className="text-right font-semibold py-2 w-32">{t('shizai.receive.qtyThisTime')}</th>
                </tr></thead>
                <tbody>
                  {(order.lines || []).map((l, i) => {
                    const remaining = (l.qty || 0) - (l.received_qty || 0);
                    return (
                      <tr key={`${l.part_no || l.name || ''}-${i}`} className="border-t border-zinc-200">
                        <td className="py-2 text-zinc-700">{l.name || '—'}</td>
                        <td className="py-2 font-mono text-xs text-zinc-500">{l.part_no || '—'}</td>
                        <td className="py-2 text-right tabular-nums">{remaining} {l.uom}</td>
                        <td className="py-2 text-right">
                          <Input type="number" data-testid={`receiving-qty-${i}`} disabled={remaining <= 0}
                            value={qtys[i] ?? ''} onChange={(e) => setQtys((p) => ({ ...p, [i]: e.target.value }))}
                            className="h-9 w-28 ml-auto text-right" placeholder="0" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {canWrite && (
                <div className="flex justify-end">
                  <Button onClick={confirmReceive} disabled={busy} data-testid="receiving-confirm-btn" className="h-11 bg-teal-700 hover:bg-teal-800">
                    <Package size={16} className="mr-1.5" /> {t('shizai.receive.confirm')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
